import {
  courseCompletionFormDocumentSchema,
  learnerDocumentMutationReceiptSchema,
  learnerDocumentReadResponseSchema,
  learnerDocumentResponseSchema,
  type LearnerDocumentContext,
} from '@entities/learner-document';
import { queryOptions } from '@tanstack/react-query';
import { z } from 'zod';
import { api } from '@shared/api/client';
import { createQuery } from '@shared/lib/query-helpers';

const canonicalUuid = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/iu;

const contextParams = (context: LearnerDocumentContext) => ({
  context_kind: context.kind,
  context_id: context.id,
});

export const learnerDocumentKeys = {
  all: ['learner-document'] as const,
  detail: (
    profileRef: string,
    documentId: string,
    context: LearnerDocumentContext,
    viewerAccessScope?: string
  ) =>
    [...learnerDocumentKeys.all, { profileRef, documentId, context, viewerAccessScope }] as const,
  completionForm: (
    profileRef: string,
    documentId: string,
    context: LearnerDocumentContext,
    viewerAccessScope: string
  ) =>
    [
      ...learnerDocumentKeys.all,
      'completion-form',
      { profileRef, documentId, context, viewerAccessScope },
    ] as const,
};

const completionFormReadSchema = z.object({ data: courseCompletionFormDocumentSchema });
const strongEtagSchema = z.string().regex(/^"sha256-[a-f0-9]{64}"$/u);
const signingReceiptSchema = z.object({
  data: z.object({
    signatureId: z.uuid(),
    documentId: z.uuid(),
    registrationId: z.uuid(),
    signedAt: z.iso.datetime(),
    signedRepresentationEtag: strongEtagSchema,
  }),
});

export const completionFormQuery = (input: {
  profileRef: string;
  documentId: string;
  context: LearnerDocumentContext;
  viewerAccessScope: string;
}) =>
  queryOptions({
    queryKey: learnerDocumentKeys.completionForm(
      input.profileRef,
      input.documentId,
      input.context,
      input.viewerAccessScope
    ),
    queryFn: async ({ signal }) => {
      const response = await api.getWithMetadata(
        `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/documents/${encodeURIComponent(input.documentId)}`,
        {
          params: contextParams(input.context),
          schema: completionFormReadSchema,
          signal,
          timeout: 60_000,
        }
      );
      if (
        response.data.data.id !== input.documentId ||
        response.data.data.context.id !== contextParams(input.context).context_id
      )
        throw new Error('Completion form identity mismatch');
      return {
        document: response.data.data,
        etag: strongEtagSchema.parse(response.headers.get('etag')),
      };
    },
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

export const signCompletionForm = async (input: {
  profileRef: string;
  documentId: string;
  context: LearnerDocumentContext;
  etag: string;
  idempotencyKey: string;
  viewerAccessScope: string;
}) =>
  await api.post(
    `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/documents/${encodeURIComponent(input.documentId)}/sign`,
    {},
    {
      params: contextParams(input.context),
      headers: {
        'If-Match': strongEtagSchema.parse(input.etag),
        'Idempotency-Key': input.idempotencyKey,
      },
      schema: signingReceiptSchema,
      timeout: 60_000,
    }
  );

export const updateCompletionFormResponse = async (input: {
  viewerAccessScope: string;
  profileRef: string;
  documentId: string;
  context: LearnerDocumentContext;
  responseId: string;
  value: boolean;
  etag: string;
  idempotencyKey: string;
}) =>
  await api.put(
    `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/documents/${encodeURIComponent(input.documentId)}/responses/${encodeURIComponent(input.responseId)}`,
    { value: input.value },
    {
      params: contextParams(input.context),
      headers: {
        'If-Match': strongEtagSchema.parse(input.etag),
        'Idempotency-Key': input.idempotencyKey,
      },
      schema: learnerDocumentMutationReceiptSchema,
      timeout: 60_000,
    }
  );

const createCompletionFormReceiptSchema = z.object({
  data: z.object({ documentId: z.uuid(), operationId: z.uuid() }),
});

export const createCompletionForm = async (input: {
  profileRef: string;
  registrationId: string;
  certificationTypeId: string;
  idempotencyKey: string;
}) =>
  await api.post(
    `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/documents`,
    {
      document_type: 'course_completion_form',
      context: { kind: 'registration', id: input.registrationId },
      certification_type_id: input.certificationTypeId,
    },
    {
      headers: { 'Idempotency-Key': input.idempotencyKey },
      schema: createCompletionFormReceiptSchema,
      timeout: 60_000,
    }
  );

const completionFormActionSchema = z.object({
  data: z.object({
    document_id: z.uuid().nullable(),
    document_type: z.literal('course_completion_form'),
    state: z.enum(['not_started', 'draft', 'approved', 'unavailable']),
    context: z.object({ kind: z.literal('registration'), id: z.uuid() }),
    display: z.object({ label: z.string() }),
    permitted_actions: z.array(z.enum(['provision', 'read'])),
    certificationTypes: z.array(z.object({ id: z.uuid(), label: z.string() })),
  }),
});

export const completionFormActionQuery = (input: {
  profileRef: string;
  registrationId: string;
  viewerAccessScope: string;
}) =>
  queryOptions({
    queryKey: [...learnerDocumentKeys.all, 'completion-form-action', input] as const,
    queryFn: async ({ signal }) => {
      const response = await api.get(
        `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/documents/actions/provision`,
        {
          params: {
            context_kind: 'registration',
            context_id: input.registrationId,
            document_type: 'course_completion_form',
          },
          schema: completionFormActionSchema,
          signal,
          timeout: 60_000,
        }
      );
      if (response.data.context.id !== input.registrationId)
        throw new Error('Registration mismatch');
      return response.data;
    },
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

export const learnerDocumentQuery = (input: {
  profileRef: string;
  documentId: string;
  context: LearnerDocumentContext;
  viewerAccessScope?: string;
}) =>
  createQuery({
    queryKey: learnerDocumentKeys.detail(
      input.profileRef,
      input.documentId,
      input.context,
      input.viewerAccessScope
    ),
    path: `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/documents/${encodeURIComponent(input.documentId)}`,
    schema: learnerDocumentReadResponseSchema.refine(
      ({ data }) =>
        (!canonicalUuid.test(input.context.id) || data.context.id === input.context.id) &&
        (data.document_type !== 'course_completion_form' || data.id === input.documentId)
    ),
    requestOptions: { params: contextParams(input.context), timeout: 60_000 },
    gcTime: 0,
    staleTime: 0,
  });

export const provisionLearnerDocument = async (input: {
  profileRef: string;
  documentType: 'medical_questionnaire';
  context: LearnerDocumentContext;
  idempotencyKey: string;
}) =>
  await api.post(
    `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/documents`,
    { document_type: input.documentType, context: input.context },
    {
      headers: { 'Idempotency-Key': input.idempotencyKey },
      schema: learnerDocumentResponseSchema,
      timeout: 60_000,
    }
  );

export const putLearnerDocumentResponse = async (input: {
  profileRef: string;
  documentId: string;
  context: LearnerDocumentContext;
  responseId: string;
  value: boolean;
  revision: number;
  idempotencyKey: string;
}) =>
  await api.put(
    `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/documents/${encodeURIComponent(input.documentId)}/responses/${encodeURIComponent(input.responseId)}`,
    { value: input.value },
    {
      params: contextParams(input.context),
      headers: {
        'Idempotency-Key': input.idempotencyKey,
        'If-Match': `"${String(input.revision)}"`,
      },
      schema: learnerDocumentMutationReceiptSchema,
      timeout: 60_000,
    }
  );

export const submitLearnerDocument = async (input: {
  profileRef: string;
  documentId: string;
  context: LearnerDocumentContext;
  revision: number;
  idempotencyKey: string;
}) =>
  await api.post(
    `/api/v1/user-profiles/${encodeURIComponent(input.profileRef)}/documents/${encodeURIComponent(input.documentId)}/submit`,
    {},
    {
      params: contextParams(input.context),
      headers: {
        'Idempotency-Key': input.idempotencyKey,
        'If-Match': `"${String(input.revision)}"`,
      },
      schema: learnerDocumentMutationReceiptSchema,
      timeout: 60_000,
    }
  );
